// App-level providers
import React from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/hooks/useAuth";
import { CreativePromptProvider } from "@/contexts/CreativePromptContext";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { ThemeProvider as NivoThemeProvider } from "@nivo/theming";
import i18n from "@/lib/i18n";
import { MemoryWatchdogProvider } from "@/components/MemoryWatchdogProvider";

// Configure QueryClient with memory-optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Garbage collection time - remove cached data after 10 minutes of no observers
      gcTime: 10 * 60 * 1000,
      // Consider data stale after 2 minutes
      staleTime: 2 * 60 * 1000,
      // Retry failed queries up to 2 times
      retry: 2,
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
    },
  },
});

// Register service worker on app load
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        // Check for updates less frequently to reduce performance impact
        setInterval(() => {
          registration.update();
        }, 300000); // Check every 5 minutes instead of 1
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
  });
}

interface AppProvidersProps {
  children: React.ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <MemoryWatchdogProvider>
        <HelmetProvider>
          <I18nextProvider i18n={i18n}>
            <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
              <NivoThemeProvider theme={{}}>
                <TooltipProvider>
                <Sonner />
                <Toaster />
                <AuthProvider>
                  <CreativePromptProvider>
                    {children}
                  </CreativePromptProvider>
                </AuthProvider>
                </TooltipProvider>
              </NivoThemeProvider>
            </ThemeProvider>
          </I18nextProvider>
        </HelmetProvider>
      </MemoryWatchdogProvider>
    </QueryClientProvider>
  );
};
